

import { useState, useEffect } from "react"
import { useHistory, useParams, Link } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import SpinnerLoader from "../../../components/common/SpinnerLoader"
import Header from "../../../components/admin/common/Header"

const ViewLegalDocument = () => {
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(true)
  const [agreementStats, setAgreementStats] = useState(null)

  const { documentId } = useParams()
  const history = useHistory()
  const authInfo = useSelector((state) => state.auth.authInfo)
  const userInfo = useSelector((state) => state.auth.userInfo)

  useEffect(() => {
    fetchDocument()
    fetchAgreementStats()
  }, [documentId])

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`admin/legaldocument/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        setDocument(response.data.data)
      }
    } catch (error) {
      console.error("Error fetching document:", error)
      toast.error("Failed to fetch document")
      history.push("/admin/legal-documents")
    } finally {
      setLoading(false)
    }
  }

  const fetchAgreementStats = async () => {
    try {
      // This would be a separate endpoint to get agreement statistics
      // For now, we'll simulate it
      setAgreementStats({
        totalAgreements: 0,
        activeAgreements: 0,
        recentAgreements: [],
      })
    } catch (error) {
      console.error("Error fetching agreement stats:", error)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to deactivate this document?")) {
      return
    }

    try {
      const response = await axios.delete(`admin/legaldocument/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        toast.success("Document deactivated successfully")
        history.push("/admin/legal-documents")
      }
    } catch (error) {
      console.error("Error deleting document:", error)
      toast.error(error.response?.data?.message || "Failed to delete document")
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Not set"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatCategoryName = (category) => {
    return category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const getCategoryBadgeClass = (category) => {
    const badgeClasses = {
      terms_conditions: "bg-primary",
      privacy_policy: "bg-info",
      disclaimer: "bg-warning",
      ip_management: "bg-danger",
      cookie_policy: "bg-secondary",
      refund_policy: "bg-success",
      community_guidelines: "bg-dark",
      seller_agreement: "bg-purple",
      buyer_agreement: "bg-orange",
      api_terms: "bg-teal",
    }
    return badgeClasses[category] || "bg-secondary"
  }

  if (loading || !document) {
    return <SpinnerLoader />
  }

  return (
    <>
    <Header />
    <div className="view-legal-document" style={{ minHeight: "100vh", overflowY: "auto", padding: '18px 100px' }}>
      <div className="container-fluid" style={{ paddingBottom: "2rem" }}>
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center mb-2">
                  <span className={`badge ${getCategoryBadgeClass(document.category)} me-3`}>
                    {formatCategoryName(document.category)}
                  </span>
                  <span className={`badge ${document.isActive ? "bg-success" : "bg-secondary"}`}>
                    {document.isActive ? "Active" : "Inactive"}
                  </span>
                  {document.requiresAgreement && (
                    <span className="badge bg-warning text-dark ms-2">
                      <i className="fas fa-handshake me-1"></i>
                      Requires Agreement
                    </span>
                  )}
                </div>
                <h1 className="mb-1">{document.title}</h1>
                <p className="text-muted mb-0">
                  Version {document.version} • Created by {document.createdBy?.name} • Last updated{" "}
                  {formatDate(document.updatedAt)}
                </p>
              </div>
              <div className="d-flex gap-2">
                <Link to={`/admin/legal-documents/edit/${documentId}`} className="btn btn-outline-primary">
                  Edit
                </Link>
                <Link to={`/admin/legal-documents/versions/${documentId}`} className="btn btn-outline-info">
                  Versions
                </Link>
                {/* <div className="dropdown">
                  <button className="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                    <i className="fas fa-ellipsis-v"></i>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <button className="dropdown-item text-danger" onClick={handleDelete}>
                        <i className="fas fa-trash me-2"></i>
                        Deactivate Document
                      </button>
                    </li>
                  </ul>
                </div> */}
                <button onClick={() => history.goBack()} className="btn btn-outline-secondary">
                  Back
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-8">
                {/* Document Content */}
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Document Content</h5>
                  </div>
                  <div className="card-body" style={{ maxHeight: "600px", overflowY: "auto" }}>
                    {document.type === "custom_upload" && document.uploadedFile ? (
                      <div className="text-center py-5">
                        <i className="fas fa-file-download fa-3x text-muted mb-3"></i>
                        <h5>Uploaded File</h5>
                        <p className="text-muted mb-3">
                          <strong>{document.uploadedFile.fileName}</strong>
                          <br />
                          Size: {(document.uploadedFile.fileSize / 1024 / 1024).toFixed(2)} MB
                          <br />
                          Type: {document.uploadedFile.fileType}
                        </p>
                        <a
                          href={document.uploadedFile.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          <i className="fas fa-download me-2"></i>
                          Download File
                        </a>
                      </div>
                    ) : (
                      <div
                        className="document-content"
                        dangerouslySetInnerHTML={{ __html: document.content }}
                        style={{
                          lineHeight: "1.6",
                          fontSize: "16px",
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* SEO Information */}
                {document.seoTags && (
                  <div className="card mt-4">
                    <div className="card-header">
                      <h5 className="mb-0">SEO Information</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="text-muted small">Meta Title</label>
                            <div>{document.seoTags.metaTitle || "Not set"}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Canonical URL</label>
                            <div>{document.seoTags.canonicalUrl || "Not set"}</div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="text-muted small">Meta Description</label>
                            <div>{document.seoTags.metaDescription || "Not set"}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Keywords</label>
                            <div className="mb-3">
                              {document.seoTags.keywords && document.seoTags.keywords.length > 0
                                ? document.seoTags.keywords.map((keyword) => (
                                    <div key={keyword} className="">
                                      {keyword}
                                    </div>
                                  ))
                                : "Not set"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Notes */}
                {document.adminNotes && (
                  <div className="card mt-4">
                    <div className="card-header">
                      <h5 className="mb-0">Admin Notes</h5>
                    </div>
                    <div className="card-body">
                      <p className="mb-0">{document.adminNotes}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-lg-4">
                {/* Document Details */}
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Document Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="text-muted small">Document Code</label>
                      <div className="fw-bold">{document.documentCode}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Category</label>
                      <div>{formatCategoryName(document.category)}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Type</label>
                      <div className="text-capitalize">{document.type.replace(/_/g, " ")}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Version</label>
                      <div className="fw-bold">{document.version}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Status</label>
                      <div>
                        <span className={`badge ${document.isActive ? "bg-success" : "bg-secondary"}`}>
                          {document.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Word Count</label>
                      <div>{document.wordCount || 0}</div>
                    </div>
                    {document.estimatedReadTime > 0 && (
                      <div className="mb-3">
                        <label className="text-muted small">Estimated Read Time</label>
                        <div>{document.estimatedReadTime} minutes</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Visibility & Access */}
                <div className="card mt-4">
                  <div className="card-header">
                    <h5 className="mb-0">Visibility & Access</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="text-muted small">Visibility Scope</label>
                      <div>
                        {document.visibilityScope.map((scope) => (
                          <span key={scope} className="badge bg-primary me-1 mb-1">
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Effective From</label>
                      <div>{formatDate(document.effectiveFrom)}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Expires At</label>
                      <div>{formatDate(document.expiresAt)}</div>
                    </div>
                  </div>
                </div>

                {/* Agreement Settings */}
                {document.requiresAgreement && (
                  <div className="card mt-4">
                    <div className="card-header">
                      <h5 className="mb-0">Agreement Settings</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="text-muted small">Requires Agreement</label>
                        <div>
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Yes
                        </div>
                      </div>
                      {document.agreementTriggers && document.agreementTriggers.length > 0 && (
                        <div className="mb-3">
                          <label className="text-muted small">Agreement Triggers</label>
                          <div>
                            {document.agreementTriggers.map((trigger) => (
                              <span key={trigger} className="badge bg-info me-1 mb-1">
                                {trigger.replace(/_/g, " ")}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {agreementStats && (
                        <div className="mb-3">
                          <label className="text-muted small">Agreement Statistics</label>
                          <div>
                            <small className="d-block">Total Agreements: {agreementStats.totalAgreements}</small>
                            <small className="d-block">Active Agreements: {agreementStats.activeAgreements}</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* IP Category */}
                {document.category === "ip_management" && document.ipCategory && (
                  <div className="card mt-4">
                    <div className="card-header">
                      <h5 className="mb-0">IP Management</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="text-muted small">IP Category</label>
                        <div className="text-capitalize">{document.ipCategory.replace(/_/g, " ")}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Compliance Information */}
                {document.complianceInfo && (
                  <div className="card mt-4">
                    <div className="card-header">
                      <h5 className="mb-0">Compliance Information</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="text-muted small">Jurisdiction</label>
                        <div>{document.complianceInfo.jurisdiction || "Not specified"}</div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Law Reference</label>
                        <div>{document.complianceInfo.lawReference || "Not specified"}</div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Last Review Date</label>
                        <div>{formatDate(document.complianceInfo.lastReviewDate)}</div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Next Review Date</label>
                        <div>{formatDate(document.complianceInfo.nextReviewDate)}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Creation & Modification */}
                <div className="card mt-4">
                  <div className="card-header">
                    <h5 className="mb-0">Creation & Modification</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="text-muted small">Created By</label>
                      <div>{document.createdBy?.name}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Created At</label>
                      <div>{formatDate(document.createdAt)}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Last Modified By</label>
                      <div>{document.lastModifiedBy?.name}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Last Modified At</label>
                      <div>{formatDate(document.updatedAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default ViewLegalDocument